import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { Receipt, AlertTriangle } from "lucide-react";

export function FleetBookTable({ teamId, refreshKey }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!teamId) return;
    axios.get(`${API}/teams/${teamId}/fleet-book`)
      .then(res => setData(res.data))
      .catch(() => setData(null));
  }, [teamId, refreshKey]);

  const owned = (data?.aircraft || []).filter(a => a.owned);
  if (!data || owned.length === 0) return null;

  return (
    <Card data-testid="fleet-book-table">
      <CardHeader className="card-header-flight-deck">
        <CardTitle className="flex items-center gap-2">
          <Receipt className="w-5 h-5" />
          {t("fleetBook.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {data.warnings?.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="fleet-book-warnings">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle className="w-4 h-4" />
              {t("fleetBook.warningsTitle")}
            </p>
            {data.warnings.map(w => (
              <p key={w.aircraft_id} className="text-xs text-amber-700" data-testid={`fleet-book-warning-${w.aircraft_id}`}>
                {w.fully_depreciated
                  ? t("fleetBook.warningDepreciated", { id: w.aircraft_id, type: w.type_name, age: w.age })
                  : t("fleetBook.warningAging", { id: w.aircraft_id, type: w.type_name, years: w.years_to_retirement, limit: w.age_limit })}
              </p>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">ID</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.cost")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.accumulated")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.netValue")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.perQuarter")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.salePrice")}</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-slate-500">{t("fleetBook.saleResult")}</th>
              </tr>
            </thead>
            <tbody>
              {owned.map(a => (
                <tr key={a.aircraft_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`fleet-book-row-${a.aircraft_id}`}>
                  <td className="py-2 px-3">
                    <span className="font-mono text-xs">{a.aircraft_id}</span>
                    <span className="block text-xs text-slate-500">
                      {a.type_name} · {Number(a.age).toFixed(1)}y / {a.age_limit}y
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(a.purchase_price)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-500">-{formatCurrency(a.accumulated_depreciation)}</td>
                  <td className="py-2 px-3 text-right font-mono font-semibold">{formatCurrency(a.net_book_value)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-500">{formatCurrency(a.quarterly_depreciation)}</td>
                  <td className="py-2 px-3 text-right font-mono">{formatCurrency(a.estimated_sale_price)}</td>
                  <td className={`py-2 px-3 text-right font-mono ${a.estimated_sale_result < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                    {a.estimated_sale_result < 0 ? "" : "+"}{formatCurrency(a.estimated_sale_result)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 font-semibold" data-testid="fleet-book-totals">
                <td className="py-2 px-3 text-xs uppercase text-slate-500">{t("fleetBook.total")}</td>
                <td className="py-2 px-3 text-right font-mono">{formatCurrency(data.totals.purchase_price)}</td>
                <td className="py-2 px-3 text-right font-mono text-slate-500">-{formatCurrency(data.totals.accumulated_depreciation)}</td>
                <td className="py-2 px-3 text-right font-mono">{formatCurrency(data.totals.net_book_value)}</td>
                <td className="py-2 px-3 text-right font-mono text-slate-500">{formatCurrency(data.totals.quarterly_depreciation)}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-slate-500">{t("fleetBook.saleHint")}</p>
      </CardContent>
    </Card>
  );
}
